import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  Administrator,
  AdminRoleName,
  CreatedAdministrator,
  PasswordReset,
} from "../api/types";
import { Shell } from "../app/Shell";
import { useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { ErrorNotice } from "../components/ui";
import { AccountActionDialog, type AccountAction } from "./AccountActionDialog";
import { CreateAdministratorDialog } from "./CreateAdministratorDialog";
import { ROLE_ORDER, roleLabel } from "./roles";
import { RolesDialog } from "./RolesDialog";
import { TemporaryPasswordDialog } from "./TemporaryPasswordDialog";
import { UsersTable } from "./UsersTable";
import { USERS_PAGE_SIZE, useUsersList } from "./useUsers";
import "./users.css";

const STATUSES = new Set(["active", "disabled"]);

/**
 * Users Management: who can use this dashboard, and what each of them may do.
 *
 * ONE LIST, and every narrowing is applied by the server — this is a page, so a
 * filter in the browser would search the rows in front of the operator and
 * report that somebody on page two does not exist.
 *
 * THERE IS NO DELETE, on purpose, and no control that looks like one. An
 * administrator is the actor on audit rows, stock movements and publish events;
 * `Disable` is the end of the lifecycle and the account stays here, named and
 * attributable.
 *
 * Every action is confirmed with the acting owner's own password and every one
 * of them signs the target out. Both facts are said in the dialogs rather than
 * discovered afterwards.
 */
export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const { email: currentEmail, can } = useAuth();
  const canManage = can(CAP.usersManage);

  const [creating, setCreating] = useState(false);
  const [rolesFor, setRolesFor] = useState<Administrator | null>(null);
  const [acting, setActing] = useState<{
    administrator: Administrator;
    action: AccountAction;
  } | null>(null);
  // The plaintext of a generated password, held for exactly as long as the
  // dialog showing it is open. Nothing else in this application ever holds one.
  const [issued, setIssued] = useState<{
    title: string;
    displayName: string;
    email: string;
    password: string;
  } | null>(null);

  const search = params.get("search") ?? "";
  const rawStatus = params.get("status") ?? "";
  const status = STATUSES.has(rawStatus)
    ? (rawStatus as "active" | "disabled")
    : undefined;
  const rawRole = params.get("role") ?? "";
  const role = ROLE_ORDER.includes(rawRole as AdminRoleName)
    ? (rawRole as AdminRoleName)
    : undefined;
  const page = Number(params.get("page") ?? "1") || 1;

  const users = useUsersList({
    // Trimmed HERE rather than as the operator types: the URL keeps what was
    // typed, and the request asks for what it means. Trimming per keystroke
    // would make a space impossible to type into a display-name search.
    search: search.trim() || undefined,
    status,
    role,
    page,
    page_size: USERS_PAGE_SIZE,
  });
  const data = users.data;

  /** WHICH LIST a targeted dialog was opened from.
   *
   * One deterministic string over the effective query — the four things that
   * decide which administrators are on screen. It is derived from the URL, so
   * it moves for a typed filter, a status or role change, a page, the sidebar's
   * own link, and browser Back and Forward alike: there is nothing else to keep
   * in step with, and no global state to get out of step.
   */
  const listContext = `${search.trim()}|${status ?? ""}|${role ?? ""}|${page}`;

  /** A targeted dialog belongs to the list it was opened from, and to no other.
   *
   * A destructive dialog left open over a list that has since changed is a
   * dialog whose target the operator can no longer see — they filtered to one
   * administrator and are looking at a confirmation naming a different one.
   * That the mutation would still have gone to the right id does not make it
   * safe; the operator is the one being misled, and they are the one pressing
   * the button. So the selection is dropped the moment the context moves.
   *
   * Adjusted DURING RENDER rather than in an effect, deliberately: an effect
   * runs after the commit, so there would be one painted frame in which an
   * actionable confirmation sat over a list it no longer belongs to. React
   * re-renders immediately on a set during render, so no such frame exists.
   *
   * `creating` is untouched — Create Administrator is not bound to a row — and
   * so is `issued`, which holds a password shown exactly once and must not be
   * destroyed by a filter change.
   */
  const [openedFrom, setOpenedFrom] = useState(listContext);
  if (openedFrom !== listContext) {
    setOpenedFrom(listContext);
    setRolesFor(null);
    setActing(null);
  }

  /** The same list, answered again — and the chosen administrator is gone.
   *
   * A refetch of the SAME query does not close anything on its own: an
   * administrator whose row came back is still on screen, and the dialog goes
   * on being about them with their refreshed details. But an id the page no
   * longer carries has left the operator's view, so the dialog closes rather
   * than hovering over a list that does not contain its target — and it NEVER
   * moves to whoever is now in that place.
   */
  useEffect(() => {
    if (!data) return;
    const onThisPage = (adminId: string) =>
      data.items.some((row) => row.admin_id === adminId);

    setRolesFor((current) =>
      current && !onThisPage(current.admin_id) ? null : current,
    );
    setActing((current) =>
      current && !onThisPage(current.administrator.admin_id) ? null : current,
    );
  }, [data]);

  /** The chosen administrator, re-read from the current page BY THEIR ID.
   *
   * NEVER by position, by name, by email or by the row that now happens to sit
   * where the chosen one sat — substituting whatever is in that place is how a
   * security action ends up pointed at somebody nobody selected.
   *
   * The snapshot fallback is now only a bridge across the single render between
   * a page arriving without this id and the effect above closing the dialog. It
   * is not a way for a dialog to outlive its list.
   */
  function chosen(selected: Administrator): Administrator {
    return (
      data?.items.find((row) => row.admin_id === selected.admin_id) ?? selected
    );
  }

  /** Is this row the account this browser is signed in as?
   *
   * Recognition, not binding: the session deliberately carries no admin id, so
   * the address is the only signal available — and it is only ever used to
   * decide what to OFFER. Every action is sent against `admin_id`.
   */
  function isSelf(administrator: Administrator): boolean {
    return currentEmail !== null && administrator.email === currentEmail;
  }

  /** Any change to what is being asked restarts paging. */
  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  function onCreated(created: CreatedAdministrator) {
    setCreating(false);
    setIssued({
      title: "Administrator created",
      displayName: created.administrator.display_name,
      email: created.administrator.email,
      password: created.temporary_password,
    });
  }

  function onReset(result: PasswordReset) {
    const target = acting?.administrator;
    setActing(null);
    setIssued({
      title: "Password reset",
      displayName: target?.display_name ?? "This administrator",
      email: target?.email ?? "",
      password: result.temporary_password,
    });
  }

  const narrowed = Boolean(search || status || role);

  return (
    <Shell
      title="Users"
      actions={
        canManage ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            Create administrator
          </button>
        ) : null
      }
    >
      <div className="stack">
        <p className="muted">
          Changing somebody&apos;s roles, disabling them or resetting their
          password signs them out of every session. They receive their new
          authority the next time they sign in.
        </p>

        <div className="row users__toolbar">
          <label className="users__label" htmlFor="users-search">
            Search
          </label>
          {/* THE URL IS THE ONE SOURCE OF TRUTH for what is being asked, and
              these controls render it. `defaultValue` made this box a second
              one: it kept whatever had been typed into it, so a query that
              changed underneath — a Back, a link to /users, any navigation
              this screen did not make — left a filter term displayed over a
              table that was no longer filtered by it. On a screen whose
              buttons revoke authority, an operator reading a narrowed list
              that is not narrowed is the whole defect. */}
          <input
            id="users-search"
            type="search"
            placeholder="Name or email"
            value={search}
            onChange={(event) => update({ search: event.target.value })}
          />

          <label className="users__label" htmlFor="users-status">
            Status
          </label>
          <select
            id="users-status"
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value })}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>

          <label className="users__label" htmlFor="users-role">
            Role
          </label>
          <select
            id="users-role"
            value={role ?? ""}
            onChange={(event) => update({ role: event.target.value })}
          >
            <option value="">Any role</option>
            {ROLE_ORDER.map((value) => (
              <option key={value} value={value}>
                {roleLabel(value)}
              </option>
            ))}
          </select>

          <div className="spacer" />
          {data ? (
            <span className="muted num" role="status">
              {data.total} administrator{data.total === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        {users.isError ? (
          <div className="users__failure">
            <ErrorNotice error={users.error} />
            <button
              type="button"
              className="btn"
              disabled={users.isFetching}
              onClick={() => void users.refetch()}
            >
              {users.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        <div className="panel">
          {users.isPending ? (
            <div className="empty" role="status">
              Loading administrators…
            </div>
          ) : users.isError ? null : data && data.items.length === 0 ? (
            <div className="empty">
              {narrowed
                ? "No administrators match these filters."
                : "No administrators yet."}
            </div>
          ) : data ? (
            <UsersTable
              items={data.items}
              currentEmail={currentEmail}
              canManage={canManage}
              // Refused here as well as being disabled in the table and
              // refused by the server: three places, because this is the one
              // action whose absence an operator might work around.
              onRoles={(administrator) => {
                if (isSelf(administrator)) return;
                setRolesFor(administrator);
              }}
              onAction={(administrator, action) => {
                if (isSelf(administrator)) return;
                setActing({ administrator, action });
              }}
            />
          ) : null}
        </div>

        {data && data.total_pages > 1 ? (
          <div className="row" aria-label="Pagination">
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => update({ page: String(page - 1) })}
            >
              Previous
            </button>
            <span className="muted num" role="status">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= data.total_pages}
              onClick={() => update({ page: String(page + 1) })}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {creating ? (
        <CreateAdministratorDialog
          onCreated={onCreated}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {rolesFor ? (
        <RolesDialog
          administrator={chosen(rolesFor)}
          isSelf={isSelf(rolesFor)}
          onClose={() => setRolesFor(null)}
        />
      ) : null}

      {acting ? (
        <AccountActionDialog
          administrator={chosen(acting.administrator)}
          action={acting.action}
          onReset={onReset}
          onClose={() => setActing(null)}
        />
      ) : null}

      {issued ? (
        <TemporaryPasswordDialog
          title={issued.title}
          displayName={issued.displayName}
          email={issued.email}
          password={issued.password}
          // Closing DISCARDS the plaintext. There is nowhere else it is held
          // and no way to ask for it again — only a hash of it was stored.
          onClose={() => setIssued(null)}
        />
      ) : null}
    </Shell>
  );
}
